import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  GqlAgendaItem,
  GqlAgendaItemDecision,
  GqlMeeting,
  GqlMeetingParticipant,
} from '../../../graphql/base-types.graphql';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Stack } from '@startuptools/ui/Stack';
import { Card } from '@startuptools/ui/Card';
import { Trans } from '@lingui/react';
import { useSetAiDoneMutation } from '../../../graphql/react-operations';
import { BoardMeetingDocument } from '../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../shareholders-meetings/ShareholdersMeeting.graphql';
import { AiMcProps } from './agenda-items/MeetingConcern';
import { toast } from '@startuptools/ui/toast/use-toast';
import { ApolloError } from '@apollo/client';
import { AgendaItemEditContent } from './AgendaItemEditContent';
import { Suspense } from 'react';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { RichTextInput } from '@startuptools/ui/RichTextInput';
type FormValues = {
  commentPrimaryLang: string | null;
  commentSecondaryLang: string | null;
};

export const AgendaItemEdit = ({
  agendaItem,
  meeting,
  meetingConcerns,
  agendaItems,
  onClose,
  summonsDirty,
}: {
  agendaItem: Pick<GqlAgendaItem, 'id' | 'kind' | 'commentPrimaryLang' | 'commentSecondaryLang'> & {
    agendaItemDecisions: Pick<
      GqlAgendaItemDecision,
      'id' | 'order' | 'decisionPrimaryLang' | 'decisionSecondaryLang'
    >[];
  };
  meeting: Pick<GqlMeeting, 'id' | 'primaryLang' | 'secondaryLang' | 'companyId' | 'startTime' | 'kind'> & {
    meetingParticipants: (Pick<GqlMeetingParticipant, 'id' | 'present' | 'chairman' | 'secretary' | 'adjuster'> & {
      identity: DisplayIdentity;
      proxy?: DisplayIdentity | null;
    })[];
  };
  meetingConcerns: AiMcProps[];
  agendaItems: Pick<
    GqlAgendaItem,
    'id' | 'kind' | 'order' | 'subjectPrimaryLang' | 'subjectSecondaryLang' | 'firstGroup' | 'lastGroup' | 'mandatory'
  >[];
  onClose: () => void;
  summonsDirty: boolean;
}) => {
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      commentPrimaryLang: agendaItem.commentPrimaryLang ?? null,
      commentSecondaryLang: agendaItem.commentSecondaryLang ?? null,
    },
  });

  const [doSetAiDone, { loading }] = useSetAiDoneMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });

  const doSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSetAiDone({
        variables: { companyId: meeting.companyId, input: { id: agendaItem.id, ...values, summonsDirty } },
      });
    } catch (e) {
      if (e instanceof ApolloError) {
        if (e.message === 'INVALID_AOA') {
          toast({
            title: <Trans id="Can not make decision on this agenda item" />,
            severity: 'warning',
            description: <Trans id="The connected articles of association has no changes registered." />,
          });
          return;
        }
        if (e.message === 'INVALID_AI_NO_CONTENT') {
          toast({
            title: <Trans id="Can not save agenda item" />,
            severity: 'warning',
            description: <Trans id="Add a decision or comment" />,
          });
          return;
        }

        toast({
          title: <Trans id="Error!" />,
          severity: 'danger',
          description: (
            <Trans id="Could not save agenda item, please try again and contact support if the problem persists" />
          ),
        });
      }
      return;
    }
    onClose();
  };

  return (
    <Stack gap={1} p={1}>
      <ErrorBoundary>
        <Suspense>
          <AgendaItemEditContent
            agendaItem={agendaItem}
            meeting={meeting}
            agendaItems={agendaItems}
            meetingConcerns={meetingConcerns}
            kind={agendaItem.kind}
          />
        </Suspense>
      </ErrorBoundary>
      <form onSubmit={handleSubmit(doSubmit)}>
        <Stack gap={1}>
          <Card>
            {meeting.primaryLang && (
              <Controller
                control={control}
                name="commentPrimaryLang"
                render={({ field: { value, onChange } }) => (
                  <FormControl>
                    <FormLabel>
                      <Trans id="Comment" />
                    </FormLabel>

                    <RichTextInput value={value} onChange={onChange} />
                  </FormControl>
                )}
              />
            )}
            {meeting.secondaryLang && (
              <Controller
                control={control}
                name="commentSecondaryLang"
                render={({ field: { value, onChange } }) => (
                  <FormControl>
                    <FormLabel>
                      <Trans id="Comment (eng)" />
                    </FormLabel>

                    <RichTextInput value={value} onChange={onChange} />
                  </FormControl>
                )}
              />
            )}
          </Card>
          <Stack direction="row" gap={1}>
            <Button type="submit" loading={loading}>
              <Trans id="Save" />
            </Button>
            <Button variant="outlined" color="neutral" onClick={onClose} disabled={loading}>
              <Trans id="Cancel" />
            </Button>
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
};
